/**
 * The customer's notification inbox — portal-local data.
 *
 * Read directly through Kong -> PostgREST under RLS, using the customer's own
 * JWT. There is NO BFF hop here and no hand-written authorization check in
 * front of it: the RLS policies on `public.notifications`
 * (`notifications_select_own`, `notifications_update_read_own`, both gated on
 * `portal_user_is_activated`) are the entire authorization story (ADR-0005).
 * Business data (OS, orcamentos, laudos, entregas, recebiveis) goes through
 * the BFF instead — see `./supabase.ts`'s module doc for the split.
 *
 * The database grants exactly:
 *   grant select           on public.notifications to authenticated;
 *   grant update (read_at) on public.notifications to authenticated;
 * i.e. a customer may read their own rows and update `read_at` and NOTHING
 * ELSE — not `estado`, not `titulo`. This module never attempts to write any
 * other column; PostgREST would (correctly) refuse it.
 *
 * Takes the `SupabaseClient` as a parameter rather than reaching for
 * `getSupabaseClient()` internally, following `portalUsers.ts`'s shape — that
 * makes each function the injectable seam directly, so a test can pass a stub
 * client with no `__setSupabaseClientForTests` and no React tree.
 *
 * ---------------------------------------------------------------------------
 * Mapping at the boundary.
 *
 * PostgREST returns the raw Portuguese column names, because the schema is
 * Portuguese and stays that way (CLAUDE.md §1: `titulo`, `corpo`, `estado`,
 * ...). Everything above this module is English: `NotificationRow` (below) is
 * the raw shape mirroring the actual columns, and `Notification` is the
 * mapped, English domain type every caller above this file should use.
 *
 * NEVER log `documento`, `titulo` or `corpo`. `corpo` is personal data about
 * the customer's property (ADR-0006) — inbox-only, and it must never reach a
 * push payload or a log line.
 */
import type { PostgrestError, SupabaseClient } from '@supabase/supabase-js';

const TABLE = 'notifications';

/**
 * The raw `public.notifications` row shape, as PostgREST returns it.
 * Portuguese column names on purpose — this is the one place they are
 * allowed to appear, at the mapping boundary (CLAUDE.md §1).
 */
export interface NotificationRow {
  id: string;
  portal_user_id: string;
  documento: string;
  kind: string;
  titulo: string;
  corpo: string | null;
  deep_link: string | null;
  franquia_id: string | null;
  referencia: unknown;
  dedupe_key: string | null;
  estado: 'queued' | 'sent' | 'failed' | 'read';
  created_by_staff_ref: string | null;
  created_at: string;
  read_at: string | null;
}

/** The columns this module needs. Kept narrow on purpose. */
const SELECT_COLUMNS =
  'id,portal_user_id,documento,kind,titulo,corpo,deep_link,franquia_id,referencia,dedupe_key,estado,created_by_staff_ref,created_at,read_at';

/**
 * The English domain type every caller above this module should use. Maps
 * 1:1 to `NotificationRow`, renamed to English and with nothing Portuguese
 * left in it.
 */
export interface Notification {
  id: string;
  documento: string;
  kind: string;
  title: string;
  /** Personal data (the customer's property). Inbox only — never log it, never push it. */
  body: string | null;
  deepLink: string | null;
  franquiaId: string | null;
  reference: unknown;
  state: 'queued' | 'sent' | 'failed' | 'read';
  createdAt: string;
  readAt: string | null;
}

function mapRow(row: NotificationRow): Notification {
  return {
    id: row.id,
    documento: row.documento,
    kind: row.kind,
    title: row.titulo,
    body: row.corpo,
    deepLink: row.deep_link,
    franquiaId: row.franquia_id,
    reference: row.referencia,
    state: row.estado,
    createdAt: row.created_at,
    readAt: row.read_at,
  };
}

/** Never includes a `documento`, `titulo` or `corpo` value — see module doc. */
function messageOf(error: PostgrestError | Error | unknown): string {
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message: unknown }).message;
    if (typeof message === 'string') return message;
  }
  return String(error);
}

export interface NotificationsListResult {
  data: Notification[];
  /** Present when the read failed; the caller decides how to degrade. */
  error: string | null;
}

/**
 * Lists the signed-in customer's notifications, newest first.
 *
 * Does NOT filter by `portal_user_id` as an authorization check — RLS already
 * guarantees only the caller's own rows are visible
 * (`notifications_select_own`). Ordering is the only thing this query does.
 *
 * An empty inbox is a normal outcome (`{ data: [], error: null }`), never an
 * error — mirrors CLAUDE.md §2's "zero matches and many matches are both
 * normal". A PostgREST error is caught and returned as a handled result;
 * this function never throws.
 */
export async function listNotifications(client: SupabaseClient): Promise<NotificationsListResult> {
  try {
    const { data, error } = await client
      .from(TABLE)
      .select(SELECT_COLUMNS)
      .order('created_at', { ascending: false });

    if (error) {
      return { data: [], error: error.message };
    }

    const rows = (data ?? []) as unknown as NotificationRow[];
    return { data: rows.map(mapRow), error: null };
  } catch (err) {
    return { data: [], error: messageOf(err) };
  }
}

export interface MarkNotificationReadResult {
  data: Notification | null;
  /** Present when the write failed; the caller decides how to degrade. */
  error: string | null;
}

/**
 * Marks a single notification read: sets `read_at` to now, and updates
 * NOTHING else. The database grant is column-level
 * (`grant update (read_at) on public.notifications`) — attempting to also
 * set e.g. `estado` or `titulo` would be refused, so this function never
 * asks for that.
 *
 * Scoped to the given `notificationId`; RLS (`notifications_update_read_own`)
 * additionally guarantees the row must belong to the caller, so this is not
 * itself the authorization check.
 */
export async function markNotificationRead(
  client: SupabaseClient,
  notificationId: string,
): Promise<MarkNotificationReadResult> {
  try {
    const { data, error } = await client
      .from(TABLE)
      .update({ read_at: new Date().toISOString() })
      .eq('id', notificationId)
      .select(SELECT_COLUMNS)
      .maybeSingle();

    if (error) {
      return { data: null, error: error.message };
    }

    const row = data as unknown as NotificationRow | null;
    return { data: row ? mapRow(row) : null, error: null };
  } catch (err) {
    return { data: null, error: messageOf(err) };
  }
}

export interface UnreadCountResult {
  count: number;
  /** Present when the read failed; the caller decides how to degrade. */
  error: string | null;
}

/**
 * The TanStack Query key for the unread badge.
 *
 * Lives beside the query it names, not in either page. Two separate surfaces
 * depend on it agreeing exactly — `NotificacoesList` invalidates it after
 * marking a notification read, and `DashboardLayout` reads it for the tab-bar
 * badge. Written as a literal in both places, the badge would silently stop
 * refreshing the moment one of them was edited: nothing would fail, the number
 * would just quietly go stale. Same reasoning as INVALID_CREDENTIALS_KEY.
 */
export const UNREAD_COUNT_QUERY_KEY = ['notifications', 'unreadCount'] as const;

/**
 * Counts the signed-in customer's unread notifications, for the tab-bar
 * badge. "Unread" means `read_at is null` — deliberately independent of
 * `estado`, since `estado` tracks delivery (`queued|sent|failed`) rather than
 * whether the customer has opened the message, and `read` is set alongside
 * `read_at` by the same action.
 *
 * A failed count degrades to `0` with the error surfaced, rather than
 * throwing — a badge that fails to load should disappear, not crash the tab
 * bar.
 */
export async function fetchUnreadCount(client: SupabaseClient): Promise<UnreadCountResult> {
  try {
    const { count, error } = await client
      .from(TABLE)
      .select('id', { count: 'exact', head: true })
      .is('read_at', null);

    if (error) {
      return { count: 0, error: error.message };
    }

    return { count: count ?? 0, error: null };
  } catch (err) {
    return { count: 0, error: messageOf(err) };
  }
}
